import type { InstructorResponseDto } from '@repo/types';
import React, { useState, useEffect } from 'react';
import { HiSearch, HiPlus } from 'react-icons/hi';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { TablePageLayout } from '../../components/Layout/TablePageLayout';
import {
  Button,
  Input,
  Card,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useInstructors } from '../../hooks/useInstructors';

/**
 * Instructors 컴포넌트 - 강사 목록 페이지
 */
const Instructors: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentLimit = parseInt(searchParams.get('limit') || '20', 10);
  const searchKeyword = searchParams.get('search') || '';

  const [searchQuery, setSearchQuery] = useState(searchKeyword);

  const queryParams = {
    page: currentPage,
    limit: currentLimit,
    sortBy: 'name' as const,
    sortOrder: 'ASC' as const,
    filterKey: searchKeyword ? ('search' as const) : undefined,
    filterValue: searchKeyword || undefined,
  };

  const { data, isLoading, isError, error, refetch } = useInstructors(queryParams);

  useEffect(() => {
    setSearchQuery(searchKeyword);
  }, [searchKeyword]);

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  const handleLimitChange = (newLimit: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('limit', newLimit.toString());
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleSearch = () => {
    const newParams = new URLSearchParams();
    newParams.set('page', '1');
    newParams.set('limit', currentLimit.toString());
    if (searchQuery.trim()) {
      newParams.set('search', searchQuery.trim());
    }
    setSearchParams(newParams);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('ko-KR');
  };

  if (isError) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-bold text-slate-900">오류가 발생했습니다</h3>
          <p className="mb-6 text-slate-500">
            {error instanceof Error ? error.message : '강사 목록을 불러오는데 실패했습니다.'}
          </p>
          <Button onClick={() => refetch()} variant="outline">
            다시 시도
          </Button>
        </Card>
      </div>
    );
  }

  const instructors = data?.data || [];
  const meta = data?.meta || { page: 1, totalPages: 1, total: 0, limit: 20 };
  const totalPages = meta.totalPages;
  const total = meta.total;
  const page = meta.page;
  const limit = meta.limit;

  return (
    <TablePageLayout
      title="강사 목록"
      description="강사 정보를 관리하고 조회하세요"
      action={
        <Button leftIcon={<HiPlus />} onClick={() => navigate('/dashboard/instructors/new')}>
          강사 추가
        </Button>
      }
      search={
        <>
          <div className="relative w-full lg:w-80">
            <Input
              placeholder="이름, 직업, 소개 제목으로 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              leftIcon={<HiSearch />}
              className="w-full"
            />
          </div>
          <Button onClick={handleSearch} variant="secondary">
            검색
          </Button>
        </>
      }
      isLoading={isLoading}
      currentPage={page}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      totalItems={total}
      limit={limit}
      onLimitChange={handleLimitChange}>
      {isLoading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
          <LoadingSpinner />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20 whitespace-nowrap">ID</TableHead>
              <TableHead className="min-w-[150px]">이름</TableHead>
              <TableHead>직업</TableHead>
              <TableHead className="min-w-[200px]">소개 제목</TableHead>
              <TableHead className="whitespace-nowrap">생성일</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {instructors.map((instructor: InstructorResponseDto) => (
              <TableRow key={instructor.id} onClick={() => navigate(`/dashboard/instructors/${instructor.id}`)}>
                <TableCell nowrap className="text-slate-500">
                  #{instructor.id}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {instructor.imageUrl ? (
                      <img
                        src={instructor.imageUrl}
                        alt={instructor.name}
                        className="h-10 w-10 rounded-full border border-slate-200 object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-sm font-medium text-indigo-600">
                        {instructor.name.charAt(0)}
                      </div>
                    )}
                    <span className="font-medium text-slate-900">{instructor.name}</span>
                  </div>
                </TableCell>
                <TableCell>{instructor.occupation || '-'}</TableCell>
                <TableCell>{instructor.bioTitle}</TableCell>
                <TableCell nowrap className="text-slate-500">
                  {formatDate(instructor.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {!isLoading && instructors.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-50">
            <svg className="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900">등록된 강사가 없습니다</h3>
          <p className="mt-1 text-slate-500">새로운 강사를 추가하거나 검색 조건을 변경해보세요.</p>
        </div>
      )}
    </TablePageLayout>
  );
};

export default Instructors;
